import { Button, Card, Modal, Tabs, Tooltip } from '@payaca/ambrosia';
import { useRegion } from '@/api/rest/users/useRegion';
import { useQueryClient } from '@tanstack/react-query';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import {
  getDeal,
  getInvoiceLinesByDealId,
  getInvoicesByDealId,
} from '@/utils/stateAccessors';
import {
  useAcceptedJobLineItemsForDeal,
  useDeal,
  usePaymentsForDeal,
  useProposalsForDeal,
} from '@payaca/store/hooks/appState';
import * as invoiceActions from '@payaca/store/invoices/invoicesActions';

import projectKeys from '@/api/queries/project/keyFactory';
import { useGetProjectInvoices } from '@/api/queries/project/useGetProjectInvoices';
import {
  isGrantRow,
  ProjectPaymentScheduleStage,
  useGetProjectPaymentSchedule,
} from '@/api/queries/project/useGetProjectPaymentSchedule';
import { useSelector } from '@/api/state';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import SectionHeader from '@payaca/components/sectionHeader/SectionHeader';
import {
  acceptedProposalOnDeal,
  canCreateInvoiceForDeal,
} from '@payaca/helpers/dealHelper';
import { currencyPrice, formatPercentage } from '@payaca/helpers/financeHelper';
import { getMaxInvoiceableValue } from '@payaca/helpers/grantHelper';
import { getLatestDateFromJobPayment } from '@payaca/helpers/jobPaymentHelper';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { PaymentsPermissions } from '@payaca/permissions/payments/payments.permissions';
import { requestGetDeal } from '@payaca/store/deals/dealsActions';
import {
  requestGetInvoicesForDeal,
  requestGetPaymentReconciliationRecordsForDeal,
} from '@payaca/store/invoices/invoicesActions';
import { requestGetJobPaymentsForDeal } from '@payaca/store/jobPayments/jobPaymentsActions';
import * as jobsActions from '@payaca/store/jobs/jobsActions';
import { Link } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import CreateInvoiceModal from '../createInvoiceModal/CreateInvoiceModal';
import DealPaymentReconciliationTable from '../dealPaymentReconciliationTable/DealPaymentReconciliationTable';
import { SkeletonInvoiceCard } from '../invoiceCard/SkeletonInvoiceCard';
import PaymentCard from '../paymentCard/PaymentCard';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import RecordPaymentModal from '../recordPaymentModal/RecordPaymentModal';
import './DealInvoicesPayments.sass';
import useCreateInvoice from './hooks';
import { InvoicesEmptyState } from './InvoicesEmptyState';
import { ListedInvoices } from './ListedInvoices';
import { ProjectGrantsList } from './ProjectGrantsList';
import { ProjectPaymentScheduleStepper } from './ProjectPaymentScheduleStepper';

type ModalState =
  | {
      activeModal: 'recordPayment' | null;
    }
  | {
      activeModal: 'createInvoice';
      defaultToInvoiceValue?: number;
    }
  | {
      activeModal: 'createNextInvoice';
      stage: ProjectPaymentScheduleStage;
    };

type ModalAction =
  | {
      type: 'openCreateNextInvoiceModal';
      payload: { stage: ProjectPaymentScheduleStage };
    }
  | {
      type: 'openCreateInvoiceModal';
      payload?: { defaultToInvoiceValue?: number };
    }
  | { type: 'openRecordPaymentModal' }
  | { type: 'closeModal' };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'openCreateInvoiceModal':
      return {
        activeModal: 'createInvoice',
        defaultToInvoiceValue: action.payload?.defaultToInvoiceValue,
      };
    case 'openCreateNextInvoiceModal':
      return {
        activeModal: 'createNextInvoice',
        stage: action.payload.stage,
      };
    case 'openRecordPaymentModal':
      return { activeModal: 'recordPayment' };
    case 'closeModal':
      return { activeModal: null };
    default:
      return state;
  }
};

interface Props {
  dealId: number;
  onDealUpdateSuccess: (updatedJobIds?: number[]) => void;
}

export const DealInvoicesPayments: FC<Props> = ({
  dealId,
  onDealUpdateSuccess,
}: Props) => {
  const deal = useSelector((state) => {
    return getDeal(state, dealId);
  });
  const history = useHistory();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { project } = useGetProjectByIdOverview(deal?.publicId);

  const [modalState, modalDispatch] = useReducer(modalReducer, {
    activeModal: null,
  });
  const [showPaymentReconciliationTable, setShowPaymentReconciliationTable] =
    useState(false);

  const region = useRegion();

  const payments = usePaymentsForDeal(dealId);

  useEffect(() => {
    dispatch(invoiceActions.requestGetInvoicesForDeal(dealId));
    dispatch(
      invoiceActions.requestGetPaymentReconciliationRecordsForDeal(dealId)
    );
    dispatch(invoiceActions.requestGetInvoiceLinesForDeal(dealId));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [dealId]);

  const sortedPayments = useMemo(() => {
    if (!payments) return [];

    return payments.sort((p1, p2) => {
      const p1Date = getLatestDateFromJobPayment(p1);
      const p2Date = getLatestDateFromJobPayment(p2);

      return p1Date < p2Date ? 1 : -1;
    });
  }, [payments]);

  const pendingPayments = useMemo(() => {
    return sortedPayments.filter(
      (x) => !x.paymentFailedConfirmationAt && !x.paymentCompletedConfirmationAt
    );
  }, [sortedPayments]);

  const confirmedPayments = useMemo(() => {
    return sortedPayments.filter(
      (x) =>
        !!x.paymentFailedConfirmationAt || !!x.paymentCompletedConfirmationAt
    );
  }, [sortedPayments]);

  const {
    canCreateInvoice,
    projectHasAcceptedProposal,
    canCreateProtoInvoice,
    projectHasProtoInvoice,
  } = useProjectInvoicesPaymentsValues(dealId);

  const onRecordOrConfirmPaymentSuccess = useCallback(() => {
    dispatch(requestGetDeal(dealId));
    dispatch(requestGetInvoicesForDeal(dealId));
    dispatch(requestGetJobPaymentsForDeal(dealId));
    dispatch(requestGetPaymentReconciliationRecordsForDeal(dealId));
    if (project?.publicId) {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.project(project?.publicId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.profitBreakdown(project?.publicId),
      });
    }
  }, [dispatch, dealId, queryClient, project]);

  const { data: paymentScheduleData, isLoading: isLoadingPaymentSchedule } =
    useGetProjectPaymentSchedule(project?.publicId);

  const nextInvoiceStage = paymentScheduleData?.stages?.find(
    (stage) => !stage.invoice && !isGrantRow(stage)
  );

  // Both "Invoiced" and 'To Invoice" count customer invoices only (grant invoices excluded).
  const customerInvoicedValue =
    project?.totals.customerInvoicedValue.value ?? deal?.sentInvoicedValue ?? 0;
  const toInvoiceValue = project
    ? project.totals.totalPayable.value -
      project.totals.customerInvoicedValue.value
    : (deal?.acceptedValue ?? 0) - (deal?.sentInvoicedValue ?? 0);

  const { data: invoices, isLoading: isLoadingProjectInvoices } =
    useGetProjectInvoices(project?.publicId);

  // Grant invoices are surfaced under their grant, not in the main list, so they
  // don't count towards the tab having invoices to show.
  const hasCustomerInvoices = !!invoices?.some(
    (invoice) => !invoice.isGrantInvoice
  );
  const hasAcceptedPaymentSchedule =
    paymentScheduleData?.isInSync &&
    paymentScheduleData.stages?.length &&
    projectHasAcceptedProposal;
  const hasProtoInvoice = projectHasProtoInvoice;

  const isEmpty =
    !hasCustomerInvoices && !hasAcceptedPaymentSchedule && !hasProtoInvoice;

  const isLoading = isLoadingProjectInvoices || isLoadingPaymentSchedule;

  if (!deal) return null;

  return (
    <div className="deal-invoices-payments">
      <PermissionGuard
        renderIfHasPermissions={[InvoicesPermissions.GET_INVOICES]}
      >
        <Card>
          <Card.Body className="space-y-4">
            <div className="flex gap-4 flex-wrap-reverse">
              {projectHasAcceptedProposal && (
                <div className="summary-wrapper">
                  <dl className="invoices-summary-data">
                    <div>
                      <dt>Invoiced</dt>
                      <dd>{currencyPrice(customerInvoicedValue, region)}</dd>
                    </div>
                    <div className="divider" />
                    <div>
                      <dt>To invoice</dt>
                      <dd>{currencyPrice(toInvoiceValue, region)}</dd>
                    </div>
                  </dl>
                </div>
              )}
              {!isEmpty && (
                <div className="ml-auto">
                  <PrimaryCreateAction
                    projectId={dealId}
                    modalDispatch={modalDispatch}
                  />
                </div>
              )}
            </div>
            {isLoading && <SkeletonInvoiceCard />}
            {!isLoading && isEmpty && (
              <>
                <InvoicesEmptyState />
                <div className="text-center">
                  <PrimaryCreateAction
                    projectId={dealId}
                    modalDispatch={modalDispatch}
                  />
                  {!canCreateInvoice &&
                    !canCreateProtoInvoice &&
                    !projectHasAcceptedProposal && (
                      <p>
                        You need an accepted{' '}
                        <Link to={`/deals/${dealId}/proposals`}>proposal</Link>{' '}
                        to create an invoice
                      </p>
                      // TODO: should use publicId
                    )}
                </div>
              </>
            )}

            {!isLoading && !isEmpty && (
              <>
                {hasAcceptedPaymentSchedule ? (
                  <Tabs variant="segment" defaultIndex={0}>
                    <Tabs.Panel
                      label="Payment schedule"
                      render={() => (
                        <ProjectPaymentScheduleStepper
                          stages={paymentScheduleData?.stages}
                          projectPublicId={deal.publicId}
                          onRecordPaymentSuccess={
                            onRecordOrConfirmPaymentSuccess
                          }
                          onMarkGrantAsPaidSuccess={
                            onRecordOrConfirmPaymentSuccess
                          }
                          requestCreateNextInvoice={() => {
                            if (nextInvoiceStage) {
                              modalDispatch({
                                type: 'openCreateNextInvoiceModal',
                                payload: {
                                  stage: nextInvoiceStage,
                                },
                              });
                            }
                          }}
                        />
                      )}
                    />
                    <Tabs.Panel
                      label="All"
                      render={() => (
                        <ListedInvoices
                          projectId={dealId}
                          onRecordPaymentSuccess={
                            onRecordOrConfirmPaymentSuccess
                          }
                        />
                      )}
                    />
                  </Tabs>
                ) : (
                  <ListedInvoices
                    projectId={dealId}
                    onRecordPaymentSuccess={onRecordOrConfirmPaymentSuccess}
                    excludeGrantInvoices
                  />
                )}
              </>
            )}
            {!isLoading &&
              !hasAcceptedPaymentSchedule &&
              !!project?.grants?.length && (
                <ProjectGrantsList
                  grants={project.grants}
                  invoices={invoices ?? []}
                  projectPublicId={deal.publicId}
                  onMarkGrantAsPaidSuccess={onRecordOrConfirmPaymentSuccess}
                />
              )}
          </Card.Body>
        </Card>
      </PermissionGuard>
      <PermissionGuard
        renderIfHasPermissions={[PaymentsPermissions.GET_PAYMENTS]}
      >
        <section className="payments">
          <SectionHeader
            headerComponent={<h4>Payments</h4>}
            actionsComponent={
              projectHasAcceptedProposal && (
                <PermissionGuard
                  renderIfHasPermissions={[PaymentsPermissions.RECORD_PAYMENT]}
                >
                  <div>
                    <Button
                      variant="link"
                      onClick={() =>
                        modalDispatch({ type: 'openRecordPaymentModal' })
                      }
                    >
                      Record payment
                    </Button>
                  </div>
                </PermissionGuard>
              )
            }
          />

          {!projectHasAcceptedProposal ? (
            <p className="accept-proposal-prompt-message">
              You need an accepted{' '}
              <Link to={`/projects/${deal?.publicId}/proposals`}>proposal</Link>{' '}
              or a sent invoice to record a payment.
            </p>
          ) : (
            <>
              <div className="summary-wrapper">
                <dl className="payments-summary-data">
                  <div>
                    <dt>Received</dt>
                    <dd>{currencyPrice(deal.completedPaymentValue, region)}</dd>
                  </div>
                  <div className="divider" />

                  <div>
                    <dt>Due</dt>
                    <dd>{currencyPrice(deal.dueValue, region)}</dd>
                  </div>
                </dl>
              </div>
              {/* todo sort job payments */}
              {!!deal?.jobPaymentIds?.length && (
                <ul className="payments-list">
                  {pendingPayments.map((payment, i) => {
                    return (
                      <li key={`pending-payment-${i}`}>
                        <PaymentCard
                          paymentId={payment.id}
                          onConfirmPaymentSuccess={
                            onRecordOrConfirmPaymentSuccess
                          }
                        />
                      </li>
                    );
                  })}
                  {confirmedPayments.map((payment, i) => {
                    return (
                      <li key={`confirmed-payment-${i}`}>
                        <PaymentCard
                          paymentId={payment.id}
                          onConfirmPaymentSuccess={
                            onRecordOrConfirmPaymentSuccess
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </section>
      </PermissionGuard>
      {!!confirmedPayments?.length && (
        <PermissionGuard
          renderIfHasPermissions={[
            InvoicesPermissions.GET_PAYMENT_RECONCILIATION_RECORDS,
          ]}
        >
          <section className="payment-reconciliation">
            {!showPaymentReconciliationTable && (
              <div>
                <Button
                  variant="link"
                  onClick={() => setShowPaymentReconciliationTable(true)}
                >
                  Show payment reconciliation details
                </Button>
              </div>
            )}

            {showPaymentReconciliationTable && (
              <>
                <SectionHeader
                  headerComponent={<h3>Payment reconciliation</h3>}
                />
                <DealPaymentReconciliationTable dealId={dealId} />
              </>
            )}
          </section>
        </PermissionGuard>
      )}
      <RecordPaymentModal
        dealId={dealId}
        isOpen={modalState.activeModal === 'recordPayment'}
        onClose={() => modalDispatch({ type: 'closeModal' })}
        recordPaymentCallback={onRecordOrConfirmPaymentSuccess}
        confirmPaymentCallback={onRecordOrConfirmPaymentSuccess}
      />
      {modalState.activeModal === 'createInvoice' && (
        <CreateInvoiceModal
          dealId={dealId}
          isOpen={modalState.activeModal === 'createInvoice'}
          onClose={() => modalDispatch({ type: 'closeModal' })}
          onCreateInvoiceSuccess={(invoiceId) => {
            history.push(`/projects/${dealId}/invoices/${invoiceId}`);
          }}
          defaultToInvoiceValue={modalState.defaultToInvoiceValue}
        />
      )}
      {modalState.activeModal === 'createNextInvoice' && (
        <CreateNextInvoiceModal
          dealId={dealId}
          isOpen={modalState.activeModal === 'createNextInvoice'}
          onClose={() => modalDispatch({ type: 'closeModal' })}
          onCreateInvoiceSuccess={(invoiceId) => {
            history.push(`/projects/${dealId}/invoices/${invoiceId}`);
          }}
          stage={modalState.stage}
          createInvoiceForOtherAmount={() => {
            modalDispatch({
              type: 'openCreateInvoiceModal',
              payload: {
                defaultToInvoiceValue: modalState.stage.stage?.value.value,
              },
            });
          }}
        />
      )}
    </div>
  );
};

const CreateNextInvoiceModal: FC<{
  dealId: number;
  isOpen: boolean;
  onClose: () => void;
  onCreateInvoiceSuccess: (invoiceId: number) => void;
  stage: ProjectPaymentScheduleStage;
  createInvoiceForOtherAmount: () => void;
}> = ({
  dealId,
  isOpen,
  onClose,
  onCreateInvoiceSuccess,
  stage,
  createInvoiceForOtherAmount,
}) => {
  const { createInvoice, isLoading } = useCreateInvoice(dealId, {
    onSuccess: (invoiceId) => {
      onClose();
      onCreateInvoiceSuccess(invoiceId);
    },
  });
  const derivedStage = stage.stage;
  const shareOfTotal =
    derivedStage && derivedStage.__typename !== 'DerivedGrantStage'
      ? derivedStage.shareOfTotal
      : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Create invoice`}>
      <Modal.Body>
        <p className="mb-4">
          Create next invoice according to payment schedule
        </p>

        <div className="grid grid-cols-3 grid-rows-1 gap-4">
          <div>
            <p className="supporting-body text-gray-500">Stage:</p>
            <p>{derivedStage?.description}</p>
          </div>
          {shareOfTotal != null && (
            <div>
              <p className="supporting-body text-gray-500">Percentage:</p>
              <p>{formatPercentage(shareOfTotal)}%</p>
            </div>
          )}
          <div>
            <p className="supporting-body text-gray-500">Value:</p>
            <p>{derivedStage && <CurrencyValue {...derivedStage.value} />}</p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions className="space-x-4">
          <Button
            variant="tertiary"
            onClick={() => {
              createInvoiceForOtherAmount();
            }}
          >
            Other amount
          </Button>
          {derivedStage && (
            <Button
              onClick={() => {
                createInvoice(
                  derivedStage.value.value,
                  derivedStage.__typename === 'DerivedDepositStage'
                );
              }}
              isProcessing={isLoading}
            >
              Create for <CurrencyValue {...derivedStage.value} />
            </Button>
          )}
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

const PrimaryCreateAction: FC<{
  projectId: number;
  modalDispatch: (value: ModalAction) => void;
}> = ({ projectId, modalDispatch }) => {
  const [isCreatingProtoInvoice, setIsCreatingProtoInvoice] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const deal = useDeal(projectId);

  const {
    canCreateProtoInvoice,
    canCreateNextInvoice,
    canCreateInvoice,
    nextInvoiceStage,
    isNothingLeftToInvoiceAfterGrants,
  } = useProjectInvoicesPaymentsValues(projectId);

  const { project } = useGetProjectByIdOverview(deal?.publicId);

  if (canCreateInvoice && canCreateNextInvoice) {
    return (
      <PermissionGuard
        renderIfHasPermissions={[InvoicesPermissions.CREATE_INVOICE]}
      >
        <Button
          size="sm"
          onClick={() => {
            if (nextInvoiceStage) {
              modalDispatch({
                type: 'openCreateNextInvoiceModal',
                payload: {
                  stage: nextInvoiceStage,
                },
              });
            }
          }}
        >
          Create next invoice
        </Button>
      </PermissionGuard>
    );
  }

  if (canCreateInvoice) {
    return (
      <PermissionGuard
        renderIfHasPermissions={[InvoicesPermissions.CREATE_INVOICE]}
      >
        <Button
          size="sm"
          onClick={() => {
            modalDispatch({ type: 'openCreateInvoiceModal' });
          }}
        >
          Create invoice
        </Button>
      </PermissionGuard>
    );
  }

  if (canCreateProtoInvoice) {
    return (
      <PermissionGuard
        renderIfHasPermissions={[
          InvoicesPermissions.CREATE_INVOICE,
          JobPermissions.ADD_JOB,
        ]}
      >
        <Button
          size="sm"
          isProcessing={isCreatingProtoInvoice}
          onClick={() => {
            if (isCreatingProtoInvoice) return;
            setIsCreatingProtoInvoice(true);
            dispatch(
              jobsActions.requestCreateJob(
                true,
                projectId,
                (error, response) => {
                  setIsCreatingProtoInvoice(false);

                  if (!error) {
                    history.push(
                      `/projects/${projectId}/invoices/quick/${response.id}/edit`
                    );
                  }
                },
                true
              )
            );
          }}
        >
          Create invoice
        </Button>
      </PermissionGuard>
    );
  }

  if (
    isNothingLeftToInvoiceAfterGrants ||
    (project &&
      project.totals.acceptedValue.value > 0 &&
      project.totals.acceptedValue.value -
        project.totals.invoicedValue.value ===
        0)
  ) {
    return (
      <Tooltip
        tooltipContent={
          isNothingLeftToInvoiceAfterGrants
            ? 'Nothing is left to invoice the customer once grants are deducted'
            : 'Invoices have already been created for the accepted value of this project so far'
        }
      >
        <PermissionGuard
          renderIfHasPermissions={[InvoicesPermissions.CREATE_INVOICE]}
        >
          <Button size="sm" disabled={true}>
            Create invoice
          </Button>
        </PermissionGuard>
      </Tooltip>
    );
  }

  return null;
};

const useProjectInvoicesPaymentsValues = (projectId: number) => {
  const proposals = useProposalsForDeal(projectId);
  const acceptedJobLineItems = useAcceptedJobLineItemsForDeal(projectId);

  const deal = useSelector((state) => {
    return getDeal(state, projectId);
  });

  const invoiceLines = useSelector((state) => {
    return getInvoiceLinesByDealId(state, projectId);
  });

  const invoices = useSelector((state) => {
    return getInvoicesByDealId(state, projectId);
  });

  const acceptedProposal = useMemo(
    () => acceptedProposalOnDeal(proposals),
    [proposals]
  );

  const { project } = useGetProjectByIdOverview(deal?.publicId);
  const totals = project?.totals;
  const isNothingLeftToInvoiceAfterGrants =
    !!totals &&
    totals.totalPayable.value < totals.acceptedValue.value &&
    getMaxInvoiceableValue({
      totalPayable: totals.totalPayable.value,
      invoicedValue: totals.invoicedValue.value,
      sentInvoicedValue: totals.sentInvoicedValue.value,
      customerInvoicedValue: totals.customerInvoicedValue.value,
    }) === 0;

  const canCreateInvoice = useMemo(() => {
    return (
      !deal?.archivedAt &&
      acceptedProposal &&
      !isNothingLeftToInvoiceAfterGrants &&
      canCreateInvoiceForDeal(acceptedJobLineItems, invoices, invoiceLines)
    );
  }, [
    deal,
    invoices,
    invoiceLines,
    acceptedJobLineItems,
    acceptedProposal,
    isNothingLeftToInvoiceAfterGrants,
  ]);

  const { data: paymentScheduleData, isLoading } = useGetProjectPaymentSchedule(
    deal?.publicId
  );

  const nextInvoiceStage = paymentScheduleData?.stages?.find(
    (stage) => !stage.invoice && !isGrantRow(stage)
  );

  const canCreateNextInvoice =
    canCreateInvoice && !!nextInvoiceStage && paymentScheduleData?.isInSync;

  const canCreateProtoInvoice = useMemo(() => {
    const hasCurrentProposal = proposals?.find(
      (p) => !p.inactivatedAt && !p.archivedAt
    );
    return !hasCurrentProposal && deal && deal?.version > 1;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'deal'
  }, [proposals, deal?.version]);

  const protoInvoice = proposals?.find(
    (p) => p.isProtoInvoice && !p.inactivatedAt && !p.archivedAt && !p.sentAt
  );

  return {
    canCreateProtoInvoice,
    canCreateNextInvoice,
    canCreateInvoice,
    nextInvoiceStage,
    projectHasAcceptedProposal: !!acceptedProposal,
    projectHasProtoInvoice: !!protoInvoice,
    isNothingLeftToInvoiceAfterGrants,
  };
};
